import type { LocalGraphNodes } from './CTGStep';

/**
 * Subject of the email step that runs immediately before `nodeId`, or null when this is the first
 * email step. Drives the "continue previous thread" toggle: it only shows when an earlier email
 * step exists, and the returned subject is what a thread-continuing step inherits.
 *
 * Sequential campaign steps aren't a parent→child chain: a delay group's runnable step and the
 * continuation-delay that leads to the next group are SIBLINGS under a shared delay. So the previous
 * email is either an email ancestor (a rarer email→delay chain) or an email sibling of one of the
 * delay ancestors we walk up through.
 */
export function findPreviousEmailSubject(nodes: LocalGraphNodes, nodeId: string): string | null {
  const seen = new Set<string>();
  let childId: string | null = nodeId;
  while (childId != null && !seen.has(childId)) {
    seen.add(childId);
    const parentId: string | null = nodes[childId]?.parent_id ?? null;
    if (parentId == null) break;
    const parent = nodes[parentId];
    if (parent == null) break;
    // An email ancestor (email → delay → … chain) is the previous email directly. A blank subject
    // means there's no thread subject to inherit (matches the server), so treat it as no match.
    if (parent.type === 'email') return blankToNull(parent.subject);
    // Otherwise the group's email step is a sibling of the continuation-delay branch we came up.
    const emailSibling = Object.values(nodes).find(
      (n) => n.parent_id === parentId && n.type === 'email' && n.id !== childId,
    );
    if (emailSibling != null) return blankToNull(emailSibling.subject);
    childId = parentId;
  }
  return null;
}

function blankToNull(subject: string | null): string | null {
  return subject != null && subject.trim().length > 0 ? subject : null;
}

/**
 * Subject of the email a NEW step (not yet in the graph) would thread onto, based on where it's
 * being inserted. Drives the "continue previous thread" toggle in the create editor, mirroring
 * `findPreviousEmailSubject` for existing steps.
 *
 * A new step has no node id, so we reason from the insertion anchor the way `cadenceTemplateAddStep`
 * parents it:
 * - `beforeStepId`: the new step takes the target's chain slot, so its previous email is whatever
 *   ran before the target — the same result `findPreviousEmailSubject` gives for the target.
 * - `afterStepId` pointing at a runnable step (same-group append): that anchor is the immediately
 *   preceding step, so an email anchor IS the thread to continue; otherwise walk up from it.
 * - `afterStepId` pointing at a delay / wait_for_connect (the new step's group hangs off it): an
 *   email child of the anchor (the anchor touch's own email) is the previous email; else walk up.
 *   Walking up directly from the anchor would skip that email, which is why we check its children.
 */
export function findPreviousEmailSubjectForNewStep(
  nodes: LocalGraphNodes,
  anchor: { afterStepId?: string | null; beforeStepId?: string | null },
): string | null {
  if (anchor.beforeStepId != null) return findPreviousEmailSubject(nodes, anchor.beforeStepId);

  const afterId = anchor.afterStepId;
  if (afterId == null) return null;
  const after = nodes[afterId];
  if (after == null) return null;

  // Appending a sibling into an existing group: the anchor itself is the previous step.
  if (after.type !== 'delay' && after.type !== 'wait_for_connect') {
    if (after.type === 'email') return blankToNull(after.subject);
    return findPreviousEmailSubject(nodes, afterId);
  }

  // Starting a new group off a delay / wait_for_connect: the anchor's own email child is the
  // previous email (e.g. appending a new touch after a touch whose email hangs off this delay).
  const emailChild = Object.values(nodes).find((n) => n.parent_id === afterId && n.type === 'email');
  if (emailChild != null) return blankToNull(emailChild.subject);
  return findPreviousEmailSubject(nodes, afterId);
}
